import { useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { ExpandableLink, Checkbox } from '@bwoty-web/styleguide';
import FilterGroup from './FilterGroup';

import styles from './FilterDestinations.scss';

const isSelected = (code, selected) => {
  return selected.indexOf(code) >= 0;
};

const isOpen = (country, selected) => {
  let countryIsOpen = false;

  if (isSelected(country.countryCode, selected) === true) {
    countryIsOpen = true;
  }

  country.destinations.forEach((destination) => {
    if (isSelected(destination.code, selected) === true) {
      countryIsOpen = true;
    }
  });

  return countryIsOpen;
};

export const FilterDestinations = (props) => {
  const { header, destinations, selected, onFilter } = props;

  // on checkbox click
  const updateFilter = useCallback(
    (event) => {
      const { target } = event;

      if (target) {
        const { dataset, checked } = target;

        if (dataset.country) {
          onFilter('destinations', { code: dataset.country, value: checked });
        }
      }
    },
    [onFilter],
  );

  // when clicking the expandable link, toggle the country if country has count > 0 or it's selected.
  const onLinkClick = useCallback(
    (event) => {
      const { currentTarget: target } = event;
      if (target) {
        const { name } = target;
        const country = name && destinations.find((c) => c.countryCode === name);
        if (country) {
          const currentSelected = isSelected(country.countryCode, selected);
          if (country.count || currentSelected) {
            onFilter('destinations', { code: country.countryCode, value: !currentSelected });
          }
        }
      }
    },
    [destinations, selected, onFilter],
  );

  return (
    <FilterGroup header={header}>
      <ul className={styles.root} data-test="lms-filter-destination-group">
        {destinations.map((country) => {
          const countrySelected = isSelected(country.countryCode, selected);
          const countryOpen = isOpen(country, selected);
          const disabled = !country.count && countrySelected !== true;

          return (
            <li
              key={country.countryCode}
              className={classNames(styles.destination, {
                'lms-ctr-enabled': !disabled,
              })}
            >
              <div className={styles.entry}>
                <Checkbox
                  onChange={updateFilter}
                  uniqueId={`lms-cf-${country.countryCode}`}
                  data-country={country.countryCode}
                  disabled={disabled}
                  checked={countrySelected}
                  className={styles.checkbox}
                />
                <ExpandableLink
                  isOpen={countryOpen}
                  className={styles.link}
                  onClick={onLinkClick}
                  name={country.countryCode}
                >
                  <span className={styles.name}>
                    {country.name} ({country.count || 0})
                  </span>
                </ExpandableLink>
              </div>
              <div
                className={classNames(styles.children, { [styles.visibleChildren]: countryOpen })}
              >
                {countryOpen
                  ? country.destinations.map((destination) => {
                      const destSelected = isSelected(destination.code, selected);

                      return (
                        <Checkbox
                          key={destination.code}
                          className={styles.checkbox}
                          onChange={updateFilter}
                          uniqueId={`lms-cf-${destination.code}`}
                          data-country={destination.code}
                          disabled={!destination.count && destSelected !== true}
                          checked={destSelected}
                        >
                          <span className={styles.name}>
                            {destination.name} ({destination.count || 0})
                          </span>
                        </Checkbox>
                      );
                    })
                  : null}
              </div>
            </li>
          );
        })}
      </ul>
    </FilterGroup>
  );
};

FilterDestinations.propTypes = {
  header: PropTypes.node,
  destinations: PropTypes.array,
  selected: PropTypes.array,
  onFilter: PropTypes.func,
};
